import {
  AUDITLOG_COLUMN,
  AUDITLOG_SRC,
  AuditLogRecord
} from 'core/auditLog/AuditLog';
import { AbstractAuditLogModel } from './AuditLogModel';
import {
  AuditLogManager,
  DefaultAuditLogManager
} from 'core/auditLog/AuditLogManager';
import { toast } from 'react-toastify';
import _ from 'lodash';
import i18n from 'i18n';
import formatters, { getAgencyLogLabel } from './AuditLogFormatters';
import {
  ColumnDescription,
  ExpandRowProps
} from 'components/common/Table/types';
import { renderColumn } from 'components/TableColumn/TableColumn';

export type AgencyAuditLogListRow = {
  id: number;
  [AUDITLOG_COLUMN.DETAIL]: {
    [key: string]: {
      adminOnly?: boolean;
      org: string;
      to: string;
    };
  }[];
  [AUDITLOG_COLUMN.DATE]: string;
  [AUDITLOG_COLUMN.SOURCE_INFO]: {
    id: number;
    type: AUDITLOG_SRC;
    name: string;
  };
  [AUDITLOG_COLUMN.EMAIL]: string;
  [AUDITLOG_COLUMN.EVENT]: string;
};

export class AgencyAuditLogModel extends AbstractAuditLogModel<AgencyAuditLogListRow> {
  nonExpandRowKey: number[] = [];

  constructor(
    private isAdmin: boolean,
    public agencyId: number,
    auditLogManager: AuditLogManager = new DefaultAuditLogManager()
  ) {
    super(auditLogManager);
  }

  getSearchString() {
    let searchString = super.getSearchString();
    if (!_.isEmpty(searchString)) {
      return `${searchString}`;
    }
    return '';
  }

  getIgnorePaths() {
    return [
      'addonFeatures.limitation.',
      'addonFeatures.product.',
      'addonFeatures.creative_studio.',
      // 隱藏這兩個原始備份 object 底下的所有 descendant
      'addonFeatures.__campaignLimitationOrig.',
      'addonFeatures.__campaignOrig.'
    ];
  }

  // 暫時先把 UI 上沒有出現的設定 ignore 掉，不改其他地方的程式碼
  getIgnoreFields() {
    return [
      'goGanGroupId',
      'l1ObjectId',
      'providerId',
      'sysPercent',
      'maxOrderProfit', // duplicate with 'property.addonProps.maxOrderProfit'
      // addon features company
      'addonFeatures.company.allow',
      'addonFeatures.company.creditLimit',
      'addonFeatures.company.outdoorAgency',
      // addon features campaign
      'addonFeatures.campaign.deliveryAmountConstraint',
      'addonFeatures.campaign.bidPriceBaseVal',
      'addonFeatures.campaign.bidPriceCPCMin',
      'addonFeatures.campaign.bidPriceCPMMin',
      'addonFeatures.campaign.bidPriceCPVMin',
      'addonFeatures.campaign.bidPricevCPMMin',
      'addonFeatures.campaign.bidPriceConstraint',
      'addonFeatures.campaign.roadblock',
      // addon features channel
      'addonFeatures.channel.tiktok',
      // addon features monitor
      'addonFeatures.monitor.systemProfitMonitor',
      'addonFeatures.monitor.percentLabel',
      // addon features creatives
      'addonFeatures.creatives.option_5',
      'addonFeatures.creatives.option_6',
      'addonFeatures.creatives.option_7',
      'addonFeatures.creatives.option_9',
      // addon features report
      'addonFeatures.report.booking_calendar',
      'addonFeatures.report.yahooAd',
      'addonFeatures.report.campaign_dashboard',
      // property addonProps
      'property.addonProps.outdoorAdImpresMultiply',
      'property.addonProps.outdoorMachineCount',
      'property.addonProps.defaultMonitorBudget',
      'property.addonProps.sysProfitMonitorPercent',
      // 封存渠道自動關閉 add-on 時，後端對整個 entity 做 diff，門檻欄位出現在頂層
      'sysProfitMonitorPercent'
    ];
  }

  override getFilters(): {
    [key: string]: {
      options: SelectOptions[];
      value?: string | number;
      onSelect: (campaignTarget: string | number) => void;
    };
  } {
    const filters = {};
    const generalOptions: SelectOptions[] = [];
    this.auditLogFields
      .filter(
        (field) =>
          !this.getIgnorePaths().some((ignorePath) =>
            field.includes(ignorePath)
          ) && !this.getIgnoreFields().includes(field)
      )
      .forEach((field) => {
        const label = getAgencyLogLabel(field);
        label &&
          generalOptions.push({
            label: label,
            value: field
          });
      });
    const auditLogFieldOptions = generalOptions;
    auditLogFieldOptions.unshift({
      label: i18n.t('common.placeholder.all'),
      value: ''
    });
    filters['fieldFilter'] = {
      options: auditLogFieldOptions,
      value: this.selectedAuditLogField,
      onSelect: this.setAuditLogField
    };
    return filters;
  }

  getAuditLogDetail = (messages) => {
    return messages && Array.isArray(messages)
      ? messages.filter((msg) => {
          if (
            _.some(Object.keys(msg), (key) =>
              this.getIgnorePaths().some((ignorePath) =>
                key.includes(ignorePath)
              )
            ) ||
            _.some(Object.keys(msg), (key) =>
              this.getIgnoreFields().includes(key)
            )
          ) {
            return false;
          }
          const values: any = _.defaultTo(Object.values(msg), []);
          if (values.length === 0) return true;
          const content = values[0];
          return content.adminOnly ? content.adminOnly && this.isAdmin : true;
        })
      : messages;
  };

  getAuditLogDataOfRecord = (record: AuditLogRecord) => {
    if (!record.msg || record.msg.length === 0) {
      this.nonExpandRowKey.push(record.id);
    }
    return {
      id: record.id,
      [AUDITLOG_COLUMN.DETAIL]: this.getAuditLogDetail(record.msg),
      [AUDITLOG_COLUMN.DATE]: record.ctime,
      [AUDITLOG_COLUMN.SOURCE_INFO]: {
        id: record.srcID,
        type: record.srcType,
        name: ''
      },
      [AUDITLOG_COLUMN.EMAIL]: record.email,
      [AUDITLOG_COLUMN.EVENT]: record.funcType
    };
  };

  async initAuditLog(page = 1) {
    try {
      const pageable = {
        page: page,
        sizePerPage: 10,
        sort: this.sortField,
        direction: this.sortOrder
      };
      const auditLogFromServer = await this.auditLogManager.getAgencyAuditLog(
        this.agencyId,
        pageable,
        this.getSearchString()
      );
      this.pagination = auditLogFromServer.pagination;
      this.nonExpandRowKey = [];
      this.auditLogData = auditLogFromServer.records.map(
        this.getAuditLogDataOfRecord
      );
      this.updateFilteredAuditLogData();
    } catch (e) {
      e instanceof Error && toast.error(e.message);
      this.auditLogData = [];
    }
  }

  async getAuditLogFields() {
    try {
      this.auditLogFields = await this.auditLogManager.getAgencyAuditLogFields(
        this.agencyId
      );
      this.auditLogFields.sort();
    } catch (e) {
      e instanceof Error && toast.error(e.message);
    }
  }

  updateFilteredAuditLogData() {
    this.filteredAuditLogData = this.auditLogData ? this.auditLogData : [];
    // TODO: implement filter feature later
    return this.filteredAuditLogData;
  }

  getColumnFormatter(columnName) {
    switch (columnName) {
      case AUDITLOG_COLUMN.SOURCE_INFO:
        return formatters.srcInfoFormatter;
      case AUDITLOG_COLUMN.EVENT:
        return formatters.i18nFormatter;
      default:
        return;
    }
  }

  getSubFilters(): {
    [key: string]: {
      options: SelectOptions[];
      value?: string | number;
      onSelect: (target: string | number) => void;
    };
  } {
    return {};
  }

  getColumns(): ColumnDescription<AgencyAuditLogListRow>[] {
    const definitions = Object.values(AUDITLOG_COLUMN).map((columnToRender) => {
      if (columnToRender === AUDITLOG_COLUMN.DETAIL) {
        return undefined;
      }
      return renderColumn(
        this.getAuditLogColumnDefinition(columnToRender),
        this.getColumnFormatter(columnToRender)
      );
    });
    return _.compact(definitions);
  }

  getExpandRenderer(): ExpandRowProps<AgencyAuditLogListRow, number> {
    return {
      nonExpandable: this.nonExpandRowKey,
      renderer: formatters.detailExpandRenderer
    };
  }
}
